import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionApi } from '@/services/api/subscription';
import toast from 'react-hot-toast';

// CORRECTED: Separate type imports from value imports
import type {
  SubscriptionTier,
  SubscriptionTierInfo,
} from '@/types/subscription';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const subscriptionKeys = {
  all: ['subscription'] as const,
  current: () => [...subscriptionKeys.all, 'current'] as const,
  usage: () => [...subscriptionKeys.all, 'usage'] as const,
  tiers: () => [...subscriptionKeys.all, 'tiers'] as const,
  limit: (currentCount: number, additionalCount: number) =>
    [
      ...subscriptionKeys.all,
      'limit',
      { currentCount, additionalCount },
    ] as const,
  billing: () => [...subscriptionKeys.all, 'billing'] as const,
};

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch current subscription
 */
export function useCurrentSubscription() {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: subscriptionApi.getCurrentSubscription,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch usage statistics
 */
export function useUsageStats() {
  return useQuery({
    queryKey: subscriptionKeys.usage(),
    queryFn: subscriptionApi.getUsageStats,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Fetch available tiers - SOURCE OF TRUTH for pricing/features
 */
export function useTiers() {
  return useQuery({
    queryKey: subscriptionKeys.tiers(),
    queryFn: subscriptionApi.getTiers,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Get tier config by tier name
 * Uses backend data as source of truth
 */
export function useTierConfig(tier: SubscriptionTier): {
  config: SubscriptionTierInfo | undefined;
  isLoading: boolean;
} {
  const { data: tiers, isLoading } = useTiers();
  const config = tiers?.find(t => t.tier === tier);
  return { config, isLoading };
}

/**
 * Check employee limit for adding employees
 */
export function useLimitCheck(
  currentCount: number,
  additionalCount: number = 1,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: subscriptionKeys.limit(currentCount, additionalCount),
    queryFn: () => subscriptionApi.checkLimit(currentCount, additionalCount),
    enabled,
    staleTime: 0,
  });
}

/**
 * Check employee limit for bulk add (profile selector)
 */
export function useBulkLimitCheck(
  currentCount: number,
  selectedCount: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: subscriptionKeys.limit(currentCount, selectedCount),
    queryFn: () => subscriptionApi.checkBulkLimit(currentCount, selectedCount),
    enabled: enabled && selectedCount > 0,
    staleTime: 0,
  });
}

/**
 * Fetch billing history
 */
export function useBillingHistory() {
  return useQuery({
    queryKey: subscriptionKeys.billing(),
    queryFn: subscriptionApi.getBillingHistory,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Upgrade subscription mutation
 * Includes optimistic cache update
 */
export function useUpgradeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tier: SubscriptionTier) =>
      subscriptionApi.upgradeSubscription(tier),
    onSuccess: data => {
      // Optimistically update subscription cache
      queryClient.setQueryData(subscriptionKeys.current(), data);
      // Then invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      toast.success(`Upgraded to ${data.tier} plan!`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upgrade subscription');
    },
  });
}

/**
 * Cancel subscription mutation
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: subscriptionApi.cancelSubscription,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      toast.success(
        `Subscription cancelled. Access until ${new Date(data.expires_at).toLocaleDateString()}`
      );
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel subscription');
    },
  });
}

/**
 * Regenerate API key mutation
 */
export function useRegenerateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: subscriptionApi.regenerateApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      toast.success('API key regenerated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to regenerate API key');
    },
  });
}

// ============================================================================
// COMBINED HOOKS
// ============================================================================

/**
 * Combined hook for subscription page
 */
export function useSubscriptionPage() {
  const subscription = useCurrentSubscription();
  const usage = useUsageStats();
  const tiers = useTiers();

  return {
    subscription: subscription.data,
    usage: usage.data,
    tiers: tiers.data,
    isLoading: subscription.isLoading || usage.isLoading || tiers.isLoading,
    isError: subscription.isError || usage.isError || tiers.isError,
    error: subscription.error || usage.error || tiers.error,
    refetch: () => {
      subscription.refetch();
      usage.refetch();
      tiers.refetch();
    },
  };
}

/**
 * Hook for manual entry page limit checking
 * Returns limit info with SAFE DEFAULTS if subscription fails
 */
export function useEmployeeLimit(currentCount: number) {
  const {
    data: subscription,
    isLoading: subLoading,
    isError: subError,
  } = useCurrentSubscription();
  const { data: limitCheck, refetch } = useLimitCheck(
    currentCount,
    1,
    !!subscription
  );
  const { data: tiers } = useTiers();

  // SAFE DEFAULTS: If subscription fails, allow (backend will reject if needed)
  const canAddEmployee = limitCheck?.allowed ?? true;
  const remaining = limitCheck?.remaining ?? 999;
  const limit = subscription?.employee_limit ?? -1; // Default to unlimited if unknown
  const isUnlimited = limit === -1;
  const tier = subscription?.tier ?? 'FREE';

  // Get next tier for upgrade prompts
  const tierOrder: SubscriptionTier[] = [
    'FREE',
    'BASIC',
    'PROFESSIONAL',
    'ENTERPRISE',
  ];
  const currentIndex = tierOrder.indexOf(tier);
  const nextTier =
    currentIndex < tierOrder.length - 1 ? tierOrder[currentIndex + 1] : null;
  const nextTierInfo = nextTier ? tiers?.find(t => t.tier === nextTier) : null;

  return {
    canAddEmployee,
    remaining,
    limit,
    isUnlimited,
    tier,
    nextTier,
    nextTierInfo,
    isLoading: subLoading,
    isError: subError,
    refetchLimit: refetch,
  };
}
